import { useMemo, useState } from "react";
import {
    Breadcrumb, Button, Col, Empty,
    Input, Menu, Row, Typography,
} from "antd";
import {
    AppstoreOutlined, AppstoreAddOutlined, BankOutlined, BuildOutlined,
    CheckCircleOutlined, FileTextOutlined,
    HomeOutlined, LockOutlined, MailOutlined, RightOutlined, SafetyOutlined,
    SearchOutlined, SettingOutlined, TeamOutlined, ToolOutlined, UserOutlined,
} from "@ant-design/icons";

import AppCard from "../../components/cards/AppCard";
import "../../styles/AdminPage.css";
import FormGroupsSection from "./Admin/FormGroupsSection";
import DetailGroupsSection from "./Admin/DetailGroupsSection";
import RowActionsSection from "./Admin/RowActionsSection";
import ModuleActionRulesSection from "./Admin/ModuleActionRulesSection";
import UsersSection from "./Admin/UsersSection";
import TeamsSection from "./Admin/TeamsSection";
import RolesSection from "./Admin/RolesSection";
import PermissionsPage from "./Admin/PermissionsPage";

const { Text, Title } = Typography;

/* ─────────────────────────────────────────────
DATA
───────────────────────────────────────────── */
const adminSections = [
    {
        group: "Settings",
        items: [
            {
                id: "org", label: "Organization", icon: <BankOutlined />,
                heading: "Organization Settings",
                desc: "Manage your company profile and location details",
                cards: [
                    { label: "Company Information", icon: <BankOutlined />, desc: "Manage company profile, address and business details." },
                    { label: "Location", icon: <BuildOutlined />, desc: "Manage office locations, branches and regions." },
                ],
            },
            {
                id: "users", label: "Users", icon: <UserOutlined />,
                heading: "Users",
                desc: "Manage platform users and configure their field views.",
                component: "users",
            },
            {
                id: "teams", label: "Teams", icon: <TeamOutlined />,
                heading: "Teams",
                desc: "Organize users into teams and configure field visibility per team.",
                component: "teams",
            },
            {
                id: "roles", label: "Roles", icon: <SafetyOutlined />,
                heading: "Roles",
                desc: "Control permissions and configure field visibility per role.",
                component: "roles",
            },
            {
                id: "permissions", label: "Permissions", icon: <LockOutlined />,
                heading: "Permissions",
                desc: "Manage module-wise action permissions (view, add, edit, delete, and more) per role.",
                component: "permissions",
            },
            {
                id: "templates", label: "Templates", icon: <FileTextOutlined />,
                heading: "Templates",
                desc: "Email and signature templates for communications",
                cards: [
                    { label: "Email", icon: <MailOutlined />, desc: "Manage reusable email templates." },
                    { label: "Signature", icon: <FileTextOutlined />, desc: "Configure user email signatures." },
                ],
            },
        ],
    },
    {
        group: "Clients",
        items: [
            {
                id: "clients", label: "Clients", icon: <BankOutlined />,
                heading: "Clients",
                desc: "Manage client accounts and settings",
                cards: [
                    { label: "Clients", icon: <BankOutlined />, desc: "Manage client companies and account details." },
                ],
            },
        ],
    },
    {
        group: "Forms",
        items: [
            {
                id: "form-groups",
                label: "Form Groups",
                icon: <AppstoreAddOutlined />,
                heading: "Form Groups",
                desc: "Define field groups for module add/edit forms with validations and dropdown sources.",
                component: "form-groups",
            },
            {
                id: "detail-groups",
                label: "Detail Groups",
                icon: <AppstoreOutlined />,
                heading: "Detail Groups",
                desc: "Customize the detail view per module: labels, prefix/suffix, colour, capitalization, reference lookups and merged fields.",
                component: "detail-groups",
            },
            {
                id: "row-actions",
                label: "Row Actions",
                icon: <ToolOutlined />,
                heading: "Row Actions",
                desc: "Configure which action buttons appear per row in each module's list view, scoped per role.",
                component: "row-actions",
            },
            {
                id: "action-rules",
                label: "Action Rules",
                icon: <ToolOutlined />,
                heading: "Action Rules",
                desc: "Configure per-row visibility rules for action buttons — e.g. hide 'Source Candidates' when a job is fulfilled.",
                component: "action-rules",
            },
        ],
    },
    {
        group: "Master",
        items: [
            {
                id: "jobs", label: "Jobs", icon: <AppstoreOutlined />,
                heading: "Jobs Master",
                desc: "Configure job sources and status types",
                cards: [
                    { label: "Sources", icon: <AppstoreOutlined />, desc: "Manage job source master values." },
                    { label: "Job Status", icon: <CheckCircleOutlined />, desc: "Configure job workflow status." },
                ],
            },
            {
                id: "candidates", label: "Candidates", icon: <UserOutlined />,
                heading: "Candidates Master",
                desc: "Skills, sources and work authorization settings",
                cards: [
                    { label: "Sources", icon: <AppstoreOutlined />, desc: "Manage candidate source values." },
                    { label: "Skills", icon: <SettingOutlined />, desc: "Manage skill master data." },
                    { label: "Work Authorization", icon: <SafetyOutlined />, desc: "Manage visa and authorization types." },
                ],
            },
            {
                id: "submissions", label: "Submissions", icon: <FileTextOutlined />,
                heading: "Submissions Master",
                desc: "Rejection reasons and submission status configuration",
                cards: [
                    { label: "Rejection Reasons", icon: <FileTextOutlined />, desc: "Manage common rejection reasons." },
                    { label: "Client Submission Status", icon: <CheckCircleOutlined />, desc: "Manage client submission statuses." },
                ],
            },
            {
                id: "clientmaster", label: "Client Master", icon: <BankOutlined />,
                heading: "Client Master",
                desc: "Payment terms, client types and contact types",
                cards: [
                    { label: "Payment Terms", icon: <FileTextOutlined />, desc: "Manage billing and payment terms." },
                    { label: "Client Type", icon: <AppstoreOutlined />, desc: "Classify client business types." },
                    { label: "Client Contact Type", icon: <TeamOutlined />, desc: "Manage client contact categories." },
                ],
            },
        ],
    },
];

/* ─────────────────────────────────────────────
SUB-COMPONENTS
───────────────────────────────────────────── */

/** Config items view for Settings / Master sections */
function SettingsContent({ activeSection, activeGroup, filteredCards, searchText, setSearchText, allItems, handleNav }) {
    return (
        <Row gutter={[16, 16]}>

            {/* Header */}
            <Col span={24}>
                <AppCard
                    icon={activeSection?.icon}
                    title={activeSection?.heading}
                    extra={
                        <Input
                            className="admin-search"
                            prefix={<SearchOutlined />}
                            placeholder="Search settings…"
                            allowClear
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    }
                >
                    <div className="admin-accent-bar" />
                    <Title level={5}>{activeSection?.heading}</Title>
                    <Text type="secondary">{activeSection?.desc}</Text>
                </AppCard>
            </Col>

            {/* Config items */}
            <Col xs={24} xl={16} style={{ alignSelf: "flex-start" }}>
                <AppCard
                    icon={<AppstoreOutlined />}
                    title="Configuration Items"
                    extra={
                        <Text type="secondary">
                            {filteredCards.length} item{filteredCards.length !== 1 ? "s" : ""}
                        </Text>
                    }
                >
                    <Row gutter={[14, 14]}>
                        {filteredCards.length > 0 ? (
                            filteredCards.map((card) => (
                                <Col xs={24} sm={12} xl={8} key={card.label}>
                                    <div className="admin-config-item">
                                        <div className="admin-config-top-bar" />
                                        <div className="admin-config-body">
                                            <div className="admin-config-icon">{card.icon}</div>
                                            <div className="admin-config-text">
                                                <span className="admin-config-label">{card.label}</span>
                                                <span className="admin-config-desc">{card.desc}</span>
                                            </div>
                                        </div>
                                        <div className="admin-config-divider" />
                                        <Button
                                            type="link"
                                            size="small"
                                            className="admin-config-manage"
                                            icon={<RightOutlined />}
                                            iconPosition="end"
                                        >
                                            Manage
                                        </Button>
                                    </div>
                                </Col>
                            ))
                        ) : (
                            <Col span={24}>
                                <div className="admin-config-empty">
                                    <Empty
                                        description="No settings found"
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    />
                                </div>
                            </Col>
                        )}
                    </Row>
                </AppCard>
            </Col>

            {/* Section overview */}
            <Col xs={24} xl={8} style={{ alignSelf: "flex-start" }}>
                <AppCard icon={<FileTextOutlined />} title="Section Overview">
                    <div className="admin-overview-list">
                        {adminSections
                            .map((section) => {
                                const isActive = section.group === activeGroup?.group;
                                return (
                                    <div
                                        key={section.group}
                                        className={`admin-overview-item${isActive ? " active" : ""}`}
                                        onClick={() => {
                                            const first = section.items[0];
                                            if (first) handleNav(first.id);
                                        }}
                                    >
                                        <div className="admin-overview-left">
                                            <div className="admin-overview-icon">
                                                {section.items[0]?.icon}
                                            </div>
                                            <div className="admin-overview-text">
                                                <span className="admin-overview-name">{section.group}</span>
                                                <span className="admin-overview-sub">
                                                    {section.items.length} module{section.items.length !== 1 ? "s" : ""}
                                                </span>
                                            </div>
                                        </div>
                                        <span className={`admin-overview-count${isActive ? " active" : ""}`}>
                                            {section.items.length}
                                        </span>
                                    </div>
                                );
                            })}
                    </div>
                    <div className="admin-overview-footer">
                        <span className="admin-overview-footer-label">Total configuration items</span>
                        <span className="admin-overview-footer-value">
                            {allItems.filter((i) => i.cards).reduce((acc, i) => acc + i.cards.length, 0)}
                        </span>
                    </div>
                </AppCard>
            </Col>

        </Row>
    );
}

/* ─────────────────────────────────────────────
MAIN PAGE
───────────────────────────────────────────── */
export default function AdminPage() {
    const [activeId, setActiveId] = useState("org");
    const [searchText, setSearchText] = useState("");

    const allItems = useMemo(() => adminSections.flatMap((s) => s.items), []);

    const activeSection = useMemo(
        () => allItems.find((i) => i.id === activeId),
        [allItems, activeId]
    );

    const activeGroup = useMemo(
        () => adminSections.find((s) => s.items.some((i) => i.id === activeId)),
        [activeId]
    );

    const filteredCards = useMemo(() => {
        if (!activeSection?.cards) return [];
        const q = searchText.trim().toLowerCase();
        if (!q) return activeSection.cards;
        return activeSection.cards.filter(
            (c) => c.label.toLowerCase().includes(q) || c.desc.toLowerCase().includes(q)
        );
    }, [activeSection, searchText]);

    const menuItems = useMemo(
        () =>
            adminSections.map((s) => ({
                key: s.group,
                type: "group",
                label: s.group,
                children: s.items.map((item) => ({
                    key: item.id,
                    icon: item.icon,
                    label: item.label,
                })),
            })),
        []
    );

    const handleNav = (id) => {
        setActiveId(id);
        setSearchText("");
    };

    /* Breadcrumb last crumb */
    const activeCrumb = activeSection?.heading ?? "Admin";

    return (
        <main className="admin-page">

            {/* Breadcrumb */}
            <div className="admin-breadcrumb">
                <Breadcrumb
                    items={[
                        { title: <><HomeOutlined /> Home</> },
                        { title: "Admin" },
                        // { title: activeCrumb },
                    ]}
                />
            </div>

            <Row gutter={[16, 16]} className="admin-wrapper">

                {/* ════ SIDEBAR ════ */}
                <Col xs={24} lg={6} xl={5}>
                    <AppCard title="Admin Console" icon={<SettingOutlined />}>

                        {/* Gradient banner */}
                        <div className="admin-sidebar-banner">
                            <span className="admin-sidebar-banner-title">Admin Panel</span>
                            <span className="admin-sidebar-banner-sub">Configuration Center</span>
                            <Row gutter={[8, 0]}>
                                <Col span={12}>
                                    <div className="admin-sidebar-pill">
                                        <span className="admin-sidebar-pill-label">Groups</span>
                                        <span className="admin-sidebar-pill-value">
                                            {adminSections.length}
                                        </span>
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <div className="admin-sidebar-pill">
                                        <span className="admin-sidebar-pill-label">Modules</span>
                                        <span className="admin-sidebar-pill-value">
                                            {allItems.length}
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Menu */}
                        <Menu
                            mode="inline"
                            selectedKeys={[activeId]}
                            items={menuItems}
                            onClick={({ key }) => handleNav(key)}
                            className="admin-sidebar-menu"
                        />
                    </AppCard>
                </Col>

                {/* ════ CONTENT ════ */}
                <Col xs={24} lg={18} xl={19}>
                    {activeSection?.component === "form-groups" ? (
                        <FormGroupsSection />
                    ) : activeSection?.component === "detail-groups" ? (
                        <DetailGroupsSection />
                    ) : activeSection?.component === "row-actions" ? (
                        <RowActionsSection />
                    ) : activeSection?.component === "action-rules" ? (
                        <ModuleActionRulesSection />
                    ) : activeSection?.component === "users" ? (
                        <UsersSection />
                    ) : activeSection?.component === "teams" ? (
                        <TeamsSection />
                    ) : activeSection?.component === "roles" ? (
                        <RolesSection />
                    ) : activeSection?.component === "permissions" ? (
                        <PermissionsPage />
                    ) : (
                        <SettingsContent
                            activeSection={activeSection}
                            activeGroup={activeGroup}
                            filteredCards={filteredCards}
                            searchText={searchText}
                            setSearchText={setSearchText}
                            allItems={allItems}
                            handleNav={handleNav}
                        />
                    )}
                </Col>

            </Row>
        </main>
    );
}